import React, { useState, useEffect } from 'react';
import { X, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';
import { createWithdrawalRequest, getRegistryFinancials } from '../lib/withdrawal';
import type { RegistryFinancials } from '../types/withdrawal';

interface Props {
  registryId: string;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function MoneyRequestModal({ registryId, currency, onClose, onSuccess }: Props) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [financials, setFinancials] = useState<RegistryFinancials | null>(null);
  const [loadingFinancials, setLoadingFinancials] = useState(true);

  useEffect(() => {
    loadFinancials();
  }, [registryId]);

  const loadFinancials = async () => {
    try {
      setLoadingFinancials(true);
      const data = await getRegistryFinancials(registryId);
      setFinancials(data);
    } catch (err) {
      console.error('Error loading financials:', err);
      setError('Failed to load financial information');
    } finally {
      setLoadingFinancials(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!financials) {
      setError('Financial information not loaded');
      return;
    }

    const requestAmount = parseFloat(amount);
    if (requestAmount > financials.available_balance) {
      setError(`Amount exceeds available balance of ${currency} ${financials.available_balance}`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      await createWithdrawalRequest(registryId, requestAmount);
      setSuccess(true);
      
      // Auto close after showing success message
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 3000);
    } catch (err) {
      console.error('Error creating withdrawal request:', err);
      setError(err instanceof Error ? err.message : 'Failed to create withdrawal request');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
        <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-md w-full">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-white">Request Submitted!</h3>
            <p className="text-gray-300">
              Your withdrawal request for {currency} {amount} has been submitted successfully.
            </p>
            <div className="bg-blue-900/50 p-4 rounded-lg">
              <p className="text-blue-200 text-sm">
                💰 Your money will be transferred within 2 working days after approval.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-white" />
            <h3 className="text-lg font-semibold text-white">Request Money</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/50 text-red-200 p-3 rounded-md flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loadingFinancials ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        ) : financials ? (
          <div className="space-y-6">
            {/* Financial Summary */}
            <div className="bg-gray-900/50 p-4 rounded-lg space-y-3">
              <h4 className="font-medium text-white">Financial Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Total Collected</p>
                  <p className="text-white font-medium">{currency} {financials.total_collected.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Total Withdrawn</p>
                  <p className="text-white font-medium">{currency} {financials.total_withdrawn.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Pending Requests</p>
                  <p className="text-yellow-400 font-medium">{currency} {financials.pending_requests.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Available Balance</p>
                  <p className="text-green-400 font-medium">{currency} {financials.available_balance.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Withdrawal Form */}
            {financials.available_balance > 0 ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Withdrawal Amount ({currency})
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400">{currency}</span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="pl-12 w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                      placeholder="0.00"
                      min="0.01"
                      max={financials.available_balance}
                      step="0.01"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Maximum: {currency} {financials.available_balance.toFixed(2)}
                  </p>
                </div>

                <div className="bg-blue-900/30 p-3 rounded-lg">
                  <p className="text-blue-200 text-sm">
                    ℹ️ Money will be transferred within 2 working days after approval.
                  </p>
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !amount || parseFloat(amount) <= 0}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Processing...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-400">No funds available for withdrawal</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}